import type { CoreError } from '@ingenico/api-sdk-core/common';
import {
  ALL_LNGS_NAMESPACES,
  LNGS_NAMESPACE_CONFIGURATION_PROFILE,
  LNGS_NAMESPACE_MODULES,
  LNGS_NAMESPACE_SMART_FIELDS,
} from '@ingenico/util-i18n-config';
import type {
  ArtifactCreationBody,
  ArtifactSearchRequest,
  ArtifactSimpleResource,
  ChangesInformationsDisplayed,
  ChangesPreview,
} from '@ingenico/react-form-builder';
import { Level, showNotification } from '@ingenico/react-notifications';
import { useBusinessService, useBusinessServiceRequest } from '@ingenico/react-section-utils';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import { logError } from '@ingenico/util-common';
import { useUpdateEffect } from '@react-hookz/web';
import { toArray, toString } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload, useBlocker, useNavigate } from 'react-router';
import type { ConfigurationModuleResource } from '../../../domain/models/configurationModules.model';
import { getModuleDetailsQueryError } from '../../module/module-details-content/ModuleDetailsContent.util';
import type {
  PreviewModal,
  PreviewModalState,
  UseParameterModuleTabProps,
  UseParametersTabProps,
} from './ParameterModuleTab.model';
import { initialPreviewModalState } from './ParameterModuleTab.util';
import { buildDataFileResourceSearchBody } from './dataFileResourceSearch.util';
import { DeviceManagementService } from '../../../domain/business/services/deviceManagement.service';
import { useTranslation } from 'react-i18next';
import { useDataFilePreviewClick } from '../../../hooks/useDataFilePreviewClick.hook';

export function useParameterModuleTab({
  apiFnList,
  onSave,
  onCancel,
  resetFormBuilderChanges,
  triggerFormBuilderValidation,
  getNewFormBuilderValues,
  clearChangesAfterSave,
  resetFormData,
  childRef,
  dataFileResourceIds,
  getCurrentDataFileResourceIds,
}: UseParametersTabProps): UseParameterModuleTabProps {
  const [changesCount, setChangesCount] = useState(0);
  const changeCountRef = useRef<number>(0);
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLoadingValidations, setIsLoadingValidations] = useState<boolean>(false);
  const [previewModalState, setPreviewModalState] = useState<PreviewModalState>(initialPreviewModalState);
  const [formChanges, setFormChanges] = useState<ChangesInformationsDisplayed[]>([]);
  const [isCancelDialogOpened, setCancelDialogOpenStatus] = useState<boolean>(false);
  const [cancelRedirectUrl, setCancelRedirectUrl] = useState<string>('');
  const [artifactsSearchRequest, setArtifactsSearchRequest] = useState<ArtifactSearchRequest | null>(null);
  const deviceManagementBusinessService = useBusinessService(DeviceManagementService);
  const handleDataFilePreviewClick = useDataFilePreviewClick();
  const { t } = useTranslation([
    LNGS_NAMESPACE_MODULES.COMMON,
    LNGS_NAMESPACE_CONFIGURATION_PROFILE.DEFAULT,
    LNGS_NAMESPACE_SMART_FIELDS.DEFAULT,
  ]);

  const navigate = useNavigate();

  const filesListBody = useMemo(
    () => (artifactsSearchRequest ? buildDataFileResourceSearchBody(artifactsSearchRequest) : null),
    [artifactsSearchRequest],
  );

  const [uploadedArtifacts, setUploadedArtifacts] = useState<ArtifactSimpleResource[]>([]);
  const [selectedResourceIds, setSelectedResourceIds] = useState(dataFileResourceIds);
  const currentDataFileResourceIdsRef = useRef(getCurrentDataFileResourceIds);
  currentDataFileResourceIdsRef.current = getCurrentDataFileResourceIds;

  useEffect(() => setSelectedResourceIds(dataFileResourceIds), [dataFileResourceIds]);

  const rememberUploadedArtifact = useCallback((artifact: ArtifactSimpleResource) => {
    setUploadedArtifacts((previous) => [...previous.filter(({ id }) => id !== artifact.id), artifact]);
  }, []);

  // artifacts list and creation
  const filesListQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) =>
        filesListBody ? deviceManagementService.getArtifactsListForFormBuilder(filesListBody, isRefetch) : null,
      [filesListBody],
    ),
  );

  const selectedFilesQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) =>
        deviceManagementService.getArtifactsByIdsForFormBuilder(selectedResourceIds, isRefetch),
      [selectedResourceIds],
    ),
  );

  const artifactListPayload = useMemo<ArtifactSimpleResource[]>(() => {
    const artifactsById = new Map<string, ArtifactSimpleResource>();
    for (const artifact of [
      ...uploadedArtifacts,
      ...toArray(filesListQuery.data),
      ...toArray(selectedFilesQuery.data),
    ]) {
      artifactsById.set(artifact.id, artifact);
    }
    return [...artifactsById.values()];
  }, [uploadedArtifacts, filesListQuery.data, selectedFilesQuery.data]);

  const handleChangeArtifactSearchQuery = useCallback((query: string, acceptedExtensions?: readonly string[]) => {
    setArtifactsSearchRequest({ query, acceptedExtensions });
  }, []);

  const [, setCreateArtifactForFormBuilderState] = useAsyncDataState<ArtifactSimpleResource, CoreError>();
  const handleCreateNewArtifact = useCallback(
    (payload: ArtifactCreationBody) => {
      requestAndUpdateState(
        deviceManagementBusinessService.createArtifactForFormBuilder(payload),
        setCreateArtifactForFormBuilderState,
        {
          onSuccess: (createdArtifact) => {
            rememberUploadedArtifact(createdArtifact);
            childRef.current?.hasUploadedFile(false, createdArtifact);
            showNotification({
              level: Level.Success,
              content: t('success.createArtifact', {
                ns: ALL_LNGS_NAMESPACES.SERVICE_CONFIGURATION,
              }),
            });
          },
          onError: () => {
            childRef.current?.hasUploadedFile(true);
            showNotification({
              level: Level.Error,
              content: t('error.createArtifact', {
                ns: ALL_LNGS_NAMESPACES.SERVICE_CONFIGURATION,
              }),
            });
          },
        },
      );
    },
    [childRef, deviceManagementBusinessService, rememberUploadedArtifact, setCreateArtifactForFormBuilderState, t],
  );

  const handleCreateSmartFieldArtifact = useCallback(
    (payload: ArtifactCreationBody, onSuccess?: (artifact: ArtifactSimpleResource) => void, onError?: () => void) => {
      requestAndUpdateState(
        deviceManagementBusinessService.createArtifactForFormBuilder(payload),
        setCreateArtifactForFormBuilderState,
        {
          onSuccess: (createdArtifact) => {
            rememberUploadedArtifact(createdArtifact);
            onSuccess?.(createdArtifact);
            showNotification({
              level: Level.Success,
              content: t('success.createArtifact', {
                ns: ALL_LNGS_NAMESPACES.SERVICE_CONFIGURATION,
              }),
            });
          },
          onError: () => {
            onError?.();
            showNotification({
              level: Level.Error,
              content: t('error.createArtifact', {
                ns: ALL_LNGS_NAMESPACES.SERVICE_CONFIGURATION,
              }),
            });
          },
        },
      );
    },
    [deviceManagementBusinessService, rememberUploadedArtifact, setCreateArtifactForFormBuilderState, t],
  );

  const handlePreviewFile = useCallback(
    (fileId: string) => handleDataFilePreviewClick(fileId),
    [handleDataFilePreviewClick],
  );

  const handleChangesCount = useCallback((changes: ChangesPreview) => {
    setChangesCount(changes.globalCount);
    setFormChanges(changes.changesDisplayed);
    // The form builder has applied the edit before emitting this event. Re-read
    // its values so replacing a DATAFILE also removes the previous resource ID.
    const currentIds = currentDataFileResourceIdsRef.current();
    setSelectedResourceIds((previous) =>
      previous.length === currentIds.length && previous.every((id, index) => id === currentIds[index])
        ? previous
        : currentIds,
    );
  }, []);

  const handleClosePreviewModal = useCallback(() => setPreviewModalState(initialPreviewModalState), []);

  const [, setUpdateConfigurationModuleByIdState] = useAsyncDataState<ConfigurationModuleResource, CoreError>();

  const restorePageState = useCallback(() => {
    setPreviewModalState(initialPreviewModalState);
  }, []);

  useEffect(() => {
    changeCountRef.current = changesCount;
  }, [changesCount]);

  useUpdateEffect(() => {
    if (Object.keys(formChanges).length === 0) {
      setPreviewModalState({ isOpen: false, mode: 'view' });
    }
  }, [formChanges]);

  const handleClickViewChanges = useCallback(() => setPreviewModalState({ isOpen: true, mode: 'view' }), []);

  const handleOpenSaveModal = useCallback(() => {
    setIsLoadingValidations(true);
    validationTimeoutRef.current = setTimeout(() => {
      // call save or validate lib function
      triggerFormBuilderValidation()
        ?.then((validationInfo) => {
          if (validationInfo.isValid) {
            setPreviewModalState({ isOpen: true, mode: 'confirm' });
          }
          return void 0;
        })
        .finally(() => setIsLoadingValidations(false))
        .catch((error) => {
          setIsLoadingValidations(false);
          logError(error as Error);
        });
    }, 0);
  }, [triggerFormBuilderValidation]);

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  const handleConfirmPreviewModalModule = useCallback(() => {
    const formBuilderValue = getNewFormBuilderValues();
    return formBuilderValue
      ? new Promise<void>((resolve, reject) => {
          requestAndUpdateState(
            apiFnList?.updateConfigurationModule(formBuilderValue.parameterSetValues) ??
              getModuleDetailsQueryError('NO_UPDATE_CONFIG_MODULE_QUERY'),
            setUpdateConfigurationModuleByIdState,
            {
              onSuccess: () => {
                resolve();
                onSave?.();
                restorePageState();
                clearChangesAfterSave();
                resetFormData?.();
              },
              onError: (error) => {
                const requestError = error as Error;
                //Since this error is being reject in a place where the same onError is implemented equally, we don't need to call onError.?()
                reject(requestError);
                // And we must reject so it's catched on the next level.
              },
            },
          );
        })
      : Promise.resolve();
  }, [
    getNewFormBuilderValues,
    apiFnList,
    setUpdateConfigurationModuleByIdState,
    onSave,
    restorePageState,
    clearChangesAfterSave,
    resetFormData,
  ]);

  const handleConfirmPreviewModal = useCallback(
    () => handleConfirmPreviewModalModule(),
    [handleConfirmPreviewModalModule],
  );

  const handleCancelParameter = useCallback(
    (shouldResetAll: boolean, parameterId?: string) => {
      resetFormBuilderChanges?.(shouldResetAll, parameterId);
    },
    [resetFormBuilderChanges],
  );

  const reviewSections = useMemo(() => formChanges, [formChanges]);

  const handleCancel = useCallback(() => {
    resetFormBuilderChanges?.(true);
    onCancel?.(!!changesCount);
  }, [onCancel, changesCount, resetFormBuilderChanges]);

  const previewModal = useMemo<PreviewModal>(
    () => ({
      isOpen: previewModalState.isOpen,
      mode: previewModalState.mode,
      handleClose: handleClosePreviewModal,
      handleConfirm: handleConfirmPreviewModal,
      sections: reviewSections,
      handleCancelParameter,
    }),
    [previewModalState, handleClosePreviewModal, handleConfirmPreviewModal, handleCancelParameter, reviewSections],
  );

  const cancelModalTranslations = useMemo<UseParameterModuleTabProps['cancelModal']['cancelModalTranslations']>(
    () => ({
      title: t('confirmation.cancel.title', { ns: LNGS_NAMESPACE_CONFIGURATION_PROFILE.DEFAULT }),
      subtitle: t('confirmation.cancel.subtitle', { ns: LNGS_NAMESPACE_CONFIGURATION_PROFILE.DEFAULT }),
      buttons: {
        cancel: t('button.cancel', { ns: LNGS_NAMESPACE_MODULES.COMMON }),
        quit: t('button.quit', { ns: LNGS_NAMESPACE_MODULES.COMMON }),
      },
    }),
    [t],
  );

  const onCancelWrapper = useCallback((localCancelRedirectUrl?: string) => {
    setCancelDialogOpenStatus(true);
    setCancelRedirectUrl(toString(localCancelRedirectUrl));
  }, []);
  const onHandleCloseCancelDialog = useCallback(() => {
    setCancelDialogOpenStatus(false);
  }, []);
  const onHandleConfirmCancelDialog = useCallback(() => {
    setCancelDialogOpenStatus(false);
    setChangesCount(0);
    changeCountRef.current = 0;
    if (cancelRedirectUrl) {
      void navigate(cancelRedirectUrl);
    } else {
      // Action when valid cancel modal. We provide a function to reset the state to initial state.
      onCancel?.(true);
    }
  }, [cancelRedirectUrl, navigate, onCancel]);

  const blocker = useBlocker(({ nextLocation, currentLocation }) => {
    return (
      changeCountRef.current > 0 &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search)
    );
  });

  useBeforeUnload(
    useCallback((event: BeforeUnloadEvent) => (changeCountRef.current > 0 ? event.preventDefault() : true), []),
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      onCancelWrapper(`${blocker.location.pathname}${blocker.location.search}`);
    }
  }, [blocker, onCancelWrapper]);

  return {
    handleChangesCount,
    previewModal,
    artifacts: {
      artifactListPayload,
      handleChangeArtifactSearchQuery,
      handleCreateNewArtifact,
      handleCreateSmartFieldArtifact,
      handlePreviewFile,
      isLoading: filesListQuery.isLoading || selectedFilesQuery.isLoading,
    },
    footer: {
      isLoadingValidations,
      handleOpenSaveModal,
      handleClickViewChanges,
      changesCount,
      handleCancel,
    },
    cancelModal: {
      isCancelDialogOpened,
      onHandleCloseCancelDialog,
      onHandleConfirmCancelDialog,
      cancelModalTranslations,
    },
  };
}
